import React, { useEffect, useState } from "react";
import { Button, Form, Input, Select, Table, message } from "antd";
import { fetchCountries } from "../country/FetchCountries";
import { useSelector } from "react-redux";
import { fetchStates } from "./FetchStates";
import { EditOutlined } from "@ant-design/icons";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { token } from "../../constants/Constants";
import { create_state, update_state } from "../../endpoints/API";

const State = () => {
  const isUser = useSelector((state) => state.user.user); // Fetch user data from Redux

  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [countries, setCountries] = useState([]); // State to store country data
  const [states, setStates] = useState([]); // State to store states data
  const [form] = Form.useForm();
  const [editingStateId, setEditingStateId] = useState(null); // Track editing state ID

  // Fetch countries and states when the component mounts
  useEffect(() => {
    const getCountriesAndStates = async () => {
      try {
        await fetchCountries(setCountries, baseUrl, prodTestUrl);
        await fetchStates(setStates, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(error?.response?.data?.error||"Failed to fetch countries or states.");
      }
    };

    getCountriesAndStates();
  }, []);

  const onFinish = async (values) => {
    try {
      // Construct the payload
      const payload = {
        state: values.state.trim(),
        created_by: isUser.user_id,
        country: values.country,
        ...(editingStateId && { id: editingStateId }),
      };

      let response;

      if (editingStateId) {
        response = await update_state(payload);
      } else {
        response = await create_state(payload);
      }

      if (response.statusText=="OK") {
        message.success(
          editingStateId
            ? "State updated successfully!"
            : "State created successfully!"
        );

        // Fetch updated list of states
        await fetchStates(setStates, baseUrl, prodTestUrl); // Refresh states list
        form.resetFields();

        // Reset editing state ID
        setEditingStateId(null);
      } else {
        const errorData =response?.data;
        message.error(
          `Error: ${errorData.error || "Failed to create or update state."}`
        ); // Show error message
      }
    } catch (error) {
      message.error(error?.response?.data?.error||"An error occurred while processing the request."); // Handle errors
    }
  };

  const handleEdit = (state) => {
    form.setFieldsValue({ state: state.name, country: state.country.id }); // Set the form fields with the state name and country
    setEditingStateId(state.id); // Set the ID of the state to edit
  };

  const columns = [
    {
      title: "State",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: (country) => country?.name,
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      render: (_, state) => (
        <Button onClick={() => handleEdit(state)}>
          <EditOutlined />
        </Button>
      ), // Render the Edit button
    },
  ];

  return (
    <div>
      <Form form={form} onFinish={onFinish}>
        <div
          className="border flex gap-5 p-4 items-center rounded-lg shadow-md"
          style={{ padding: "16px" }}
        >
          <Form.Item
            label={<span className="font-semibold">Add State</span>}
            name="state"
            rules={[{ required: true, message: "Please enter state!" }]}
            style={{ margin: "0" }} // Reset margin for Form.Item
          >
            <Input placeholder="Enter State" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label={<span className="font-semibold">Select Country</span>}
            name="country"
            rules={[{ required: true, message: "Please select a country!" }]}
            style={{ margin: "0" }} // Reset margin for Form.Item
          >
            <Select placeholder="Select Country" style={{ width: "100%" }}>
              {countries.map((country) => (
                <Select.Option key={country.id} value={country.id}>
                  {country.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ margin: "0" }}>
            <Button type="primary" htmlType="submit">
              {editingStateId ? "Update" : "Save"}{" "}
              {/* Change button text based on editing state */}
            </Button>
          </Form.Item>
        </div>
      </Form>

      <Table
        dataSource={states} // Set dataSource to the states state
        columns={columns}
        rowKey="id" // Use ID as the unique key for rows
      />
    </div>
  );
};

export default State;
